import React, { useEffect, useState } from 'react';
import { TextField, useToast } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import MarketNonLoginPopup from '~/components/MarketNonLoginPopup';
import NonLoginRightMenu from '~/components/NonLogin/NonLoginRightMenu';
import { fetchCurrentUser, useCurrentUser } from '~/domains/auth/queries';
import ShoppingCartLink from '~/domains/cart/ShoppingCartLink';
import { useCurrentVendorInfo } from '~/domains/vendors/queries';
import { useRouterQueryByKey, useUpdateRouterQuery } from '~/hooks/url';
import BaseLayout from '~/layouts/BaseLayout';
import { useBusinessIdStore } from '~/store/global/useBusinessIdStore';
import { truncateTextWithLimit } from '~/utils/truncateText';

import CloseIcon from '~/assets/icons/ic_dismiss_search.svg';

import styles from './SearchLayout.module.css';

export type CustomSearchEvent = CustomEvent<{ value: string }>;

const SearchLayout: React.FC<{
  children: React.ReactNode;
  placeholder?: string;
  onChange?(value: string): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
}> = ({ children, onChange, placeholder = '상품을 검색하세요.', ...props }) => {
  const toast = useToast();
  const updateRouterQuery = useUpdateRouterQuery();

  const searchText = useRouterQueryByKey('searchText', 'string');

  const [searchWord, setSearchWord] = useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { isLoggedIn } = useCurrentUser();
  const { businessId } = useBusinessIdStore();
  const { data: vendorInfo } = useCurrentVendorInfo();

  const handleSearch = async () => {
    const value = inputRef.current?.value.trim() || '';

    // 검색이후 모바일에서 keyboard 숨기기
    inputRef.current?.blur();

    if (!value) {
      toast('검색어를 입력하고 다시 시도해주세요');
      return;
    }

    await updateRouterQuery({
      searchText: value,
      categoryIds: undefined,
    });
  };

  const handleClear = () => {
    void updateRouterQuery({
      searchText: undefined,
      categoryIds: undefined,
      deliveryType: undefined,
      productType: undefined,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchWord(value);
    onChange?.(value);
  };

  const handleOffVendorSearch = () => {
    void updateRouterQuery({
      vendorId: undefined,
    });
  };

  const onFocusSearchInput = async () => {
    const user = await fetchCurrentUser();
    trackEvent('clicked searchBar in 마켓', {
      path: window.location.pathname,
      userId: user.id,
      businessId,
      searchText: searchWord || '',
    });
  };

  /**
   * 최근 검색어, 또는 추천 검색어를 클릭하고 들어왔을 경우 예외처리
   */
  useEffect(() => {
    setSearchWord(searchText);
  }, [searchText]);

  return (
    <BaseLayout
      className={styles.layout}
      title={
        <TextField
          className={styles.input}
          autoFocus={!searchText}
          placeholder={placeholder}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              void handleSearch();
            }
          }}
          onFocus={onFocusSearchInput}
          onClear={handleClear}
          prefix={
            <>
              {vendorInfo && (
                <div
                  className="flex items-center gap-4 rounded-full bg-gray-300 py-4 pl-12 pr-8 typo-caption100-semibold"
                  role="presentation"
                  onClick={handleOffVendorSearch}
                >
                  <span>
                    {truncateTextWithLimit(vendorInfo.mallName || '', 5, '..')}
                  </span>
                  <CloseIcon />
                </div>
              )}
            </>
          }
          allowClear
          ref={inputRef}
          value={searchWord}
          onChange={handleChange}
          {...props}
        />
      }
      right={
        isLoggedIn ? (
          <ShoppingCartLink />
        ) : (
          <NonLoginRightMenu clasName="!ml-0" useMypageIcon={false} />
        )
      }
    >
      {children}
      {!isLoggedIn && <MarketNonLoginPopup />}
    </BaseLayout>
  );
};

export default SearchLayout;
